import { SKIP_WAITING, UNRECOVERABLE } from './worker-protocol';

/**
 * The page's side of the service worker (021 FR-011, FR-018; 034 T058).
 *
 * This is what `@angular/service-worker` gave the application for free, and writing it out is the
 * real cost of leaving Angular. `provideServiceWorker()` registered the worker and `SwUpdate`
 * published four things about it: whether a worker is running at all, when a newer version has
 * downloaded and is waiting, when the stored copy is broken beyond repair, and two commands —
 * check, and activate. Nothing else in `update.store.ts` ever touched the browser API.
 *
 * So the interface below is exactly that surface, and `update.store.ts` is unchanged in shape
 * because of it: the store still talks to a small object it is handed, rather than to
 * `navigator.serviceWorker` directly. The seam is what makes the store testable at all — a unit
 * test cannot install a service worker, and a suite that could would be testing the browser.
 */
export interface WorkerUpdates {
  /**
   * Whether a worker is actually running. FALSE in development, where no worker is registered at
   * all (see `vite.config.ts`), and false in a browser that has no `serviceWorker`.
   *
   * The update offer must never appear when this is false: there is no newer version to take, and
   * a banner that cannot do what it says is 020's rule broken at the top of the screen.
   */
  readonly isEnabled: boolean;
  /** A newer version has downloaded and is waiting to be activated (Angular's `VERSION_READY`). */
  onVersionReady(listener: () => void): void;
  /** The worker's stored copy is broken beyond its own repair (Angular's `unrecoverable`). */
  onUnrecoverable(listener: () => void): void;
  /** Ask the server whether a newer version exists. Resolves whether or not one does. */
  checkForUpdate(): Promise<void>;
  /** Take the waiting version. Resolves once it controls this page. */
  activateUpdate(): Promise<void>;
}

/**
 * What a client with no worker is handed — the development build, and any browser without the API.
 *
 * A null object rather than `null`, so that no caller has to remember a branch. `isEnabled` is the
 * one thing anybody asks, and the four members below can never be reached through a store that
 * respects it; they resolve rather than throw so that a caller which does reach them fails by doing
 * nothing rather than by breaking the page.
 */
export const noServiceWorker: WorkerUpdates = {
  isEnabled: false,
  onVersionReady: () => undefined,
  onUnrecoverable: () => undefined,
  checkForUpdate: () => Promise.resolve(),
  activateUpdate: () => Promise.resolve(),
};

/**
 * Register the worker and return the page's handle on it.
 *
 * **Registered at `load`, not during the first render, and that is 021's `registerWhenStable:30000`
 * kept rather than dropped.** The delay is about not competing with the initial paint — a
 * registration during boot puts the worker's own install fetches on the critical path of the first
 * screen. It is deliberately SEPARATE from update CHECKING, which is eager (`update.store.ts`), and
 * confusing the two produces a client that takes half a minute to admit it is out of date.
 *
 * What changed is the trigger and the reason is that Angular's has no counterpart: `whenStable`
 * meant "the zone has drained", and a zoneless React application has no such moment. `load` is the
 * nearest honest signal — it fires after the document's own resources are in — and the 30-second
 * ceiling that existed to stop `whenStable` waiting for ever is not needed for an event that always
 * arrives.
 */
export function registerServiceWorker(url: string): WorkerUpdates {
  if (!('serviceWorker' in navigator)) return noServiceWorker;

  const versionReady = new Set<() => void>();
  const unrecoverable = new Set<() => void>();

  /**
   * Announced ONCE per page. `statechange` and the initial poll below can both see the same waiting
   * worker, and the store's `ready` is a latch anyway — but a listener that fires twice is a
   * listener the next reader has to reason about, and this is cheaper than the reasoning.
   */
  let announced = false;
  const announce = (): void => {
    if (announced) return;
    announced = true;
    for (const listener of versionReady) listener();
  };

  navigator.serviceWorker.addEventListener('message', (event: MessageEvent<unknown>) => {
    const data = event.data;
    if (typeof data === 'object' && data !== null && (data as { type?: unknown }).type === UNRECOVERABLE) {
      for (const listener of unrecoverable) listener();
    }
  });

  const registered = new Promise<ServiceWorkerRegistration>((resolve, reject) => {
    const go = (): void => {
      navigator.serviceWorker.register(url).then(resolve, reject);
    };
    // `load` has already fired if this module is evaluated late; the event would then never come.
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  });

  const ready = registered.then((registration) => {
    /**
     * A worker is "ready" when it is INSTALLED and something else is already controlling the page.
     *
     * Both halves matter. Without the state check, a worker part-way through downloading would be
     * offered as ready and the activation would find nothing to activate. Without the controller
     * check, the very FIRST install — which is not an update at all — would raise the offer, and a
     * practitioner who had just opened the application for the first time would be told a new
     * version of it was available.
     */
    const watch = (worker: ServiceWorker | null): void => {
      if (worker === null) return;
      const look = (): void => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller !== null) announce();
      };
      worker.addEventListener('statechange', look);
      look();
    };

    // Already waiting when this page loaded — the installed-window case, where the previous session
    // downloaded a version and nobody ever reloaded to take it.
    watch(registration.waiting);
    registration.addEventListener('updatefound', () => watch(registration.installing));
    return registration;
  });

  return {
    isEnabled: true,
    onVersionReady: (listener) => void versionReady.add(listener),
    onUnrecoverable: (listener) => void unrecoverable.add(listener),
    checkForUpdate: async () => {
      const registration = await ready;
      await registration.update();
    },
    activateUpdate: async () => {
      const registration = await ready;
      const waiting = registration.waiting;
      if (waiting === null) return;

      /**
       * The listener is attached BEFORE the message is sent. A worker that skips waiting
       * immediately would otherwise change the controller before anybody was listening, and this
       * would never resolve — the update offer would then sit in its applying state for the life of
       * the page with the banner gone and no way to retry.
       */
      const activated = new Promise<void>((resolve) => {
        navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
      });
      waiting.postMessage({ type: SKIP_WAITING });
      await activated;
    },
  };
}
